import React, { useEffect, useRef } from 'react';
import '../assets/css/Background.css';

const Background = () => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // WebGL Context Creation
    const gl = canvas.getContext('webgl2', {
      antialias: false,
      depth: false,
      stencil: false, // Trailing comma compliant with modern JS
      alpha: true,
    });

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);

    if (!gl) {
      console.error('WebGL2 not supported');
      return;
    }

    // Shader Sources (Original CodePen)
    const vertexShaderSource = `#version 300 es
      precision highp float;
      in vec4 position;
      void main() {
        gl_Position = position;
      }`;

    const fragmentShaderSource = `#version 300 es
      precision highp float;
      out vec4 O;
      uniform float time;
      uniform vec2 resolution;
      
      #define FC gl_FragCoord.xy
      #define R resolution
      #define MN min(R.x,R.y)
      #define T (time+660.)
      #define S smoothstep
      #define N normalize
      #define rot(a) mat2(cos((a)-vec4(0,11,33,0)))
      
      float rnd(vec2 p) {
        p=fract(p*vec2(12.9898,78.233));
        p+=dot(p,p+34.56);
        return fract(p.x*p.y);
      }
      
      float noise(vec2 p) {
        vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f), k=vec2(1,0);
        float
        a=rnd(i),
        b=rnd(i+k),
        c=rnd(i+k.yx),
        d=rnd(i+1.);
        return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
      }
      
      float fbm(vec2 p) {
        float t=.0, a=1., h=.0; mat2 m=mat2(1.,-1.2,.2,1.2);
        for(float i=.0; i<5.; i++) {
          t+=a*noise(p);
          p*=2.*m;
          a*=.5;
          h+=a;
        }
        return t/h;
      }
      
      void main() {
        vec2 uv=(FC-.5*R)/R.y, k=vec2(0,T*.015); 
        vec3 col=vec3(1);
        uv.x+=.25;
        uv*=vec2(2,1);
        float n=fbm(uv*.28+vec2(-T*.01,0));
        n=noise(uv*3.+n*2.);
        col.r-=fbm(uv+k+n);
        col.g-=fbm(uv*1.003+k+n+.003);
        col.b-=fbm(uv*1.006+k+n+.006);
        col=mix(col,vec3(1),dot(col,vec3(.21,.71,.07)));
        col=mix(vec3(.08),col,min(time*.1,1.));
        col=clamp(col,.08,1.);
        O=vec4(col, 1);
      }`;

    // Shader Compilation
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    // Program Setup
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    // Vertex Buffer
    const vertices = new Float32Array([-1, 1, -1, -1, 1, 1, 1, -1]);
    const vertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    // Attributes/Uniforms
    const positionLocation = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const timeUniform = gl.getUniformLocation(program, 'time');
    const resolutionUniform = gl.getUniformLocation(program, 'resolution');

    // Resize Handler
    const handleResize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(resolutionUniform, canvas.width, canvas.height);
    };

    // Animation Loop
    const startTime = Date.now();
    const animate = () => {
      const currentTime = (Date.now() - startTime) * 0.001;
      gl.uniform1f(timeUniform, currentTime);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Initial Setup
    handleResize();
    animate();

    // Resize Observer
    resizeObserverRef.current = new ResizeObserver(handleResize);
    resizeObserverRef.current.observe(canvas);  
  }, []);

  return <canvas ref={canvasRef} className="webgl-background" />;
};

export default Background;
